import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { KIOSK_TIME } from "@constants/Kiosk";
import { kioskRouteEnum } from "@enums/route.enum";
import {
  UseKioskIdleTimerOptions,
  UseKioskIdleTimerReturn,
} from "@models/hooks/useKioskIdleTimer.model";

export const useKioskIdleTimer = (
  options: UseKioskIdleTimerOptions = {}
): UseKioskIdleTimerReturn => {
  const {
    enabled = true,
    onTimeout,
    redirectTo = kioskRouteEnum.Landing,
    timeout = KIOSK_TIME.DEFAULT_TIMEOUT,
    warningTime = KIOSK_TIME.DEFAULT_WARNING_TIME,
  } = options;

  const navigate = useNavigate();
  const [remainingTime, setRemainingTime] = useState(timeout);
  const [showWarning, setShowWarning] = useState(false);
  const [isIdle, setIsIdle] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const reset = useCallback(() => {
    lastActivityRef.current = Date.now();

    setRemainingTime(timeout);
    setShowWarning(false);
    setIsIdle(false);
  }, [timeout]);

  const handleActivity = useCallback(() => {
    reset();
  }, [reset]);

  useEffect(() => {
    if (enabled) {
      lastActivityRef.current = Date.now();
      setRemainingTime(timeout);
      setShowWarning(false);
      setIsIdle(false);
    }
  }, [enabled, timeout]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (showWarning) {
      return;
    }

    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    // Throttle activity detection to avoid too many resets
    let throttleTimeout: NodeJS.Timeout | null = null;
    const throttledActivity = () => {
      if (!throttleTimeout) {
        handleActivity();
        throttleTimeout = setTimeout(() => {
          throttleTimeout = null;
        }, 1000); // Throttle to once per second
      }
    };

    events.forEach(event => {
      document.addEventListener(event, throttledActivity);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledActivity);
      });
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, [handleActivity, showWarning, enabled]);

  // Main countdown timer
  useEffect(() => {
    if (!enabled || isIdle) {
      return;
    }
    timerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - lastActivityRef.current) / 1000);
      const remaining = Math.max(0, timeout - elapsed);

      setRemainingTime(remaining);

      // Show warning
      if (remaining <= warningTime && remaining > 0) {
        if (!showWarning) {
          setShowWarning(true);
        }
      } else {
        if (showWarning) {
          setShowWarning(false);
        }
      }

      // Timeout reached
      if (remaining === 0) {
        setIsIdle(true);

        if (timerRef.current) {
          clearInterval(timerRef.current);
        }

        if (onTimeout) {
          onTimeout();
        }
      }
    }, 1000); // Update every second

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [
    timeout,
    warningTime,
    showWarning,
    onTimeout,
    redirectTo,
    navigate,
    enabled,
    isIdle,
  ]);

  return {
    isIdle,
    remainingTime,
    reset,
    showWarning,
  };
};
